import type { Nullable } from "models-webgl2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import { applyCSSToNode } from "utils/dom/apply-css-to-node";
import { removeCSSFromNode } from "utils/dom/remove-css-from-node";
import { Vec2 } from "utils/math/vec2";
import {
  ImageRectComponent,
  ImageAdjustRectComponent,
  RetouchComponent,
  RetouchMaskComponent,
  RetouchPreviewComponent,
} from "../../webgl2-component";
import { Texture } from "../../texture";
import { Webgl2 } from "../../webgl2";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";

export class RetouchTool extends Webgl2Tool {
  private _prevTexture: Texture;
  private _maskTexture: Texture | null = null;
  private _lastLocalPixel: Vec2 | null = null;
  private _lastRatio: Vec2 = Vec2.one();

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  private get _texture(): Nullable<Texture> {
    return this._selectedComponent?.state.texture;
  }

  private readonly _retouchComponent: RetouchComponent;
  private readonly _maskComponent: RetouchMaskComponent;
  private readonly _previewComponent: RetouchPreviewComponent;

  private readonly _brushDiv: HTMLDivElement;

  public get brushSize() {
    return this._maskComponent.state.u_brushSize;
  }

  public set brushSize(size: number) {
    this._maskComponent.state.u_brushSize = size;
    this._handleScaleChange();
  }

  /** 0..1 */
  public get strength() {
    return this._retouchComponent.state.u_strength;
  }

  /** 0..1 */
  public set strength(value: number) {
    this._retouchComponent.state.u_strength = Math.max(0, Math.min(1, value));
  }

  /** 0..1 (1 = hard edge) */
  public get hardness() {
    return this._maskComponent.state.u_hardness;
  }

  /** 0..1 (1 = hard edge) */
  public set hardness(value: number) {
    this._maskComponent.state.u_hardness = Math.max(0, Math.min(1, value));
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    this._retouchComponent = new RetouchComponent({
      u_strength: 1,
      u_sampleRadius: 24,
    });

    this._maskComponent = new RetouchMaskComponent({
      u_brushSize: 20,
      u_hardness: 0.2,
    });

    this._previewComponent = new RetouchPreviewComponent({
      texture: null,
      u_opacity: 0.6,
    });
    this._previewComponent.style.display = "none";

    this._brushDiv = document.createElement("div");
    applyCSSToNode(this._brushDiv, {
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 1,
      pointerEvents: "none",
      boxSizing: "content-box",
      borderRadius: "50%",
      border: "1px solid #2f6fff",
      outline: "1px solid #ffffff",
    });

    this._handleScaleChange = this._handleScaleChange.bind(this);
  }

  public onAssign() {
    applyCSSToNode(this._renderer.parent, { cursor: "none" });
    this._renderer.parent?.appendChild(this._brushDiv);

    // Preview overlay is a component so it respects camera transforms & blending.
    this._renderer.addComponent(this._previewComponent);

    this._handleScaleChange();
    this._renderer.camera.events.on("scale", this._handleScaleChange);
  }

  public onUnAssign() {
    removeCSSFromNode(this._renderer.parent, "cursor");
    this._brushDiv.remove();
    this._renderer.camera.events.off("scale", this._handleScaleChange);

    this._renderer.removeComponent(this._previewComponent);

    this._lastLocalPixel = null;
    this._previewComponent.state.texture = null;
    this._previewComponent.style.display = "none";
  }

  private _handleScaleChange() {
    applyCSSToNode(this._brushDiv, {
      width: this.brushSize * this._renderer.camera.scale + "px",
      height: this.brushSize * this._renderer.camera.scale + "px",
    });
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    return this._handleMove(e);
  }

  public onMouseEnded() {
    return this._handleUp();
  }

  public onMouseOver(e: MouseEvent) {
    applyCSSToNode(this._brushDiv, {
      transform: `translate(calc(-50% + ${e.offsetX}px), calc(-50% + ${e.offsetY}px)) scale(${
        1 / window.devicePixelRatio
      })`,
    });
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    return this._handleMove(e);
  }

  public onTouchEnded() {
    return this._handleUp();
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    const texture = this._texture;
    const component = this._selectedComponent;

    if (!component || !texture) {
      console.warn("RetouchTool doesn't have selected component to operate");
      return false;
    }

    // Snapshot for undo + stable input (avoid “more strokes => stronger effect”).
    this._prevTexture = texture.clone();

    // Prepare transforms for mask painting + final apply.
    this._retouchComponent.transform.setSize(texture.textureSize);
    this._maskComponent.transform.setSize(texture.textureSize);

    // Cache ratio (component space -> texture space) for this stroke.
    this._lastRatio = texture.textureSize.clone().divide(component.transform.size);

    // Tune sampling radius in *texture pixels* (important when the image is scaled on canvas).
    this._retouchComponent.state.u_sampleRadius = Math.max(
      1,
      this.brushSize * this._lastRatio.x * 0.6,
    );

    // Create / reset mask texture.
    if (!this._maskTexture) {
      this._maskTexture = new Texture(texture.textureSize);
    } else {
      this._maskTexture.allocateTexture(texture.textureSize);
    }

    this._maskTexture.drawIntoTexture(() => {
      Webgl2.clear();
    });

    // Sync preview overlay.
    this._previewComponent.state.texture = this._maskTexture;
    this._previewComponent.style.display = "initial";
    this._previewComponent.transform.copy(component.transform);

    this._lastLocalPixel = null;

    this._drawMask(e);
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent || !this._texture) return;
    this._drawMask(e);
  }

  private _handleUp() {
    const component = this._selectedComponent;
    if (!this._texture || !component || !this._maskTexture) return;

    // Apply healing once, based on the original texture snapshot + stroke mask.
    const actionTexture = new Texture(this._prevTexture.textureSize);
    actionTexture.drawIntoTexture(() => {
      Webgl2.renderComponent(this._retouchComponent, {
        texture: this._prevTexture,
        maskTexture: this._maskTexture as Texture,
      });
    });

    // Clear mask + preview.
    this._maskTexture.drawIntoTexture(() => {
      Webgl2.clear();
    });
    this._previewComponent.style.display = "none";
    this._previewComponent.state.texture = null;
    this._lastLocalPixel = null;

    const undoTexture = this._prevTexture;

    this._renderer.commands.execute(
      new CommandAction(
        "Retouch Brush",
        () => {
          component.state.texture = actionTexture.clone();
        },
        () => {
          component.state.texture = undoTexture.clone();
        },
      ),
    );

    // Force render to reflect texture changes.
    this._renderer.render();
  }

  private _drawMask(e: MouseEvent | TouchEvent) {
    if (!this._maskTexture || !this._selectedComponent) return;

    // Keep preview aligned with active component (in case it moves/rotates).
    this._previewComponent.transform.copy(this._selectedComponent.transform);

    const currentMouseDown = this._renderer.camera.getGlobalMousePosition(e);
    const localPixel = Webgl2dRenderer.global
      .globalMouseToComponentLocal(currentMouseDown, this._selectedComponent)
      .multiply(this._lastRatio);

    const fromPixel = this._lastLocalPixel ?? localPixel;
    const toPixel = localPixel;
    const brushSizePx = this.brushSize * this._lastRatio.x;

    this._maskTexture.drawIntoTexture(() => {
      Webgl2.renderComponent(this._maskComponent, {
        texture: this._maskTexture as Texture,
        u_fromCoords: fromPixel,
        u_toCoords: toPixel,
        u_brushSize: brushSizePx,
      });
    });

    this._lastLocalPixel = toPixel;

    // NOTE: force render to see mask overlay changes.
    this._renderer.render();
  }
}

export default RetouchTool;
